import {
	NAME_REGEX,
	PASSWORD_REGEX,
	PHONE_NUMBER_REGEX
} from '@/constants/form/text-inputs/error-patterns'
import { useI18n } from 'vue-i18n'
import * as z from 'zod'

type AsYouTypeType = typeof import('libphonenumber-js').AsYouType

let AsYouType: AsYouTypeType | undefined

export function useZod() {
	const { t } = useI18n({
		useScope: 'global'
	})

	// Define validation patterns
	const zfirstName = z.string().min(2).regex(NAME_REGEX, (t('get-fiber.form.first-name.validation.pattern-failed')))
	const zLastName = z.string().min(2).regex(NAME_REGEX, (t('get-fiber.form.last-name.validation.pattern-failed')))
	const zEmail = z.string().email((t('get-fiber.form.email.validation.pattern-failed')))
	const zPhone = z.string().regex(PHONE_NUMBER_REGEX, (t('get-fiber.form.phone-number.validation.pattern-failed')))
	const zIsMobilePhone = z.boolean().default(false)
	const zOptIn = z.boolean().default(false)
	const zPassword = z.string().regex(PASSWORD_REGEX, t('get-fiber.form.password.validation.pattern-failed'))
	const zPasswordConfirm = z.string()

	const validatePhoneNumber = (phoneSchema: z.ZodString) => {
		return phoneSchema.refine(async value => {
			if (!AsYouType) {
				const lib = await import('libphonenumber-js')
				AsYouType = lib.AsYouType
			}
			const asYouType = new AsYouType('US')
			asYouType.input(value)
			return asYouType.isValid()
		}, {
			message: t('get-fiber.form.phone-number.validation.pattern-failed')
		})
	}

	// ------------------------------------
	// ------ Signup schema ---------------
	// ------------------------------------
	const signupFormSchema = z.object({
		firstName: zfirstName,
		lastName: zLastName,
		email: zEmail,
		phone: validatePhoneNumber(zPhone),
		isMobilePhone: zIsMobilePhone,
		optIn: zOptIn
	})

	type SignupFormSchema = z.output<typeof signupFormSchema>

	const signupFormNestedSchema = z.object({
		movingIn: z.boolean().default(false),
		moveInDate: z.string(),
		password: zPassword,
		passwordConfirm: zPasswordConfirm
	}).refine(value => {
		if (!value.movingIn) return true
		const date = new Date(value.moveInDate)
		return !isNaN(date.getTime()) && date >= new Date()
	}, {
		message: t('get-fiber.form.moving-in-date.validation.pattern-failed')
	}).refine((data: { password: string
		passwordConfirm: string }) => data.password === data.passwordConfirm, {
		message: t('get-fiber.form.password-confirm.validation.pattern-failed'),
		path: [ 'passwordConfirm' ]
	})

	type SignupFormNestedSchema = z.output<typeof signupFormNestedSchema>

	const signupFormState = reactive<Partial<SignupFormSchema & SignupFormNestedSchema>>({})

	// ------------------------------------
	// ------ Business Form Schema --------
	// ------------------------------------
	const businessFormSchema = z.object({
		businessName: z.string()
			.min(1, t('business.form.business-name.validation.required'))
			.max(100, t('business.form.business-name.validation.max'))
			.regex(NAME_REGEX, t('business.form.business-name.validation.pattern')),
		businessOwner: z.string()
			.min(1, t('business.form.business-owner.validation.required'))
			.max(100, t('business.form.business-owner.validation.max'))
			.regex(NAME_REGEX, t('business.form.business-owner.validation.pattern')),
		businessAddress: z.string()
			.min(1, t('business.form.business-address.validation.required'))
			.max(200, t('business.form.business-address.validation.max')),
		businessCity: z.string()
			.min(1, t('business.form.business-city.validation.required'))
			.max(50, t('business.form.business-city.validation.max'))
			.regex(NAME_REGEX, t('business.form.business-city.validation.pattern')),
		businessState: z.string()
			.min(1, t('business.form.business-state.validation.required')),
		businessZip: z.string()
			.min(1, t('business.form.business-zip.validation.required'))
			.min(5, t('business.form.business-zip.validation.min'))
			.max(10, t('business.form.business-zip.validation.max'))
			.regex(/^\d{5}(-\d{4})?$/, t('business.form.business-zip.validation.pattern')),
		businessPhone: validatePhoneNumber(
			z.string()
				.min(1, t('business.form.business-phone.validation.required'))
				.regex(PHONE_NUMBER_REGEX, t('business.form.business-phone.validation.pattern'))
		),
		businessEmail: z.string()
			.min(1, t('business.form.business-email.validation.required'))
			.email(t('business.form.business-email.validation.email'))
	})

	// Business Form State
	const businessFormState = reactive({
		businessName: '',
		businessOwner: '',
		businessAddress: '',
		businessCity: '',
		businessState: '',
		businessZip: '',
		businessPhone: '',
		businessEmail: ''
	})

	return {
		businessFormSchema,
		businessFormState,
		signupFormSchema,
		signupFormNestedSchema,
		signupFormState,
		zfirstName,
		zLastName,
		zEmail,
		zPhone,
		zIsMobilePhone,
		zOptIn,
		zPassword,
		zPasswordConfirm
	}
}
